import { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { Drawer, Grid, Layout, message } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import { AppSidebar } from "../components/AppSidebar";
import { ChangePasswordModal } from "../../components/pages";
import AuthContext from "../../context/AuthContext";
import { Logo } from "../../components/ui";
import styles from "./ProtectedLayout.module.css";

interface Props { children: ReactNode; }

/**
 * App shell: persistent gradient sidebar + a single scrolling content column.
 * The old 18vh logo header and the 5vw-padded content div are gone — page
 * chrome now lives in <PageShell/>, which each page owns.
 */
export const ProtectedLayout = ({ children }: Props) => {
  const [messageApi, contextHolder] = message.useMessage();
  const { isSuccess, isError } = useContext(AuthContext);
  const hasSucceded = useRef(false);
  const hasFailed = useRef(false);
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const isTablet = !isMobile && screens.xl === false;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [tabletNavExpanded, setTabletNavExpanded] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => window.localStorage.getItem("quattro:nav") === "collapsed"
  );

  const handleCollapse = (v: boolean) => {
    setCollapsed(v);
    window.localStorage.setItem("quattro:nav", v ? "collapsed" : "open");
  };

  const handleSidebarCollapse = (v: boolean) => {
    if (isTablet) {
      setTabletNavExpanded(!v);
      return;
    }
    handleCollapse(v);
  };

  useEffect(() => {
    if (isSuccess.status && !hasSucceded.current) {
      messageApi.open({ type: "success", content: isSuccess.message });
      hasSucceded.current = true;
    }
  }, [isSuccess.status]);

  useEffect(() => {
    if (isError.status && !hasFailed.current) {
      messageApi.open({ type: "error", content: isError.message });
      hasFailed.current = true;
    }
  }, [isError]);

  return (
    <>
      <Layout className={styles.shell} hasSider={!isMobile}>
        {contextHolder}
        {!isMobile && (
          <AppSidebar
            collapsed={isTablet ? !tabletNavExpanded : collapsed}
            onCollapse={handleSidebarCollapse}
          />
        )}
        <Layout className={styles.workspace}>
          {isMobile && (
            <header className={styles.mobileBar}>
              <button
                type="button"
                className={styles.mobileMenu}
                aria-label="Open navigation"
                onClick={() => setMobileNavOpen(true)}
              >
                <MenuOutlined aria-hidden="true" />
              </button>
              <span className={styles.mobileBrand}>
                <Logo mobile />
                <span>Quattro Support</span>
              </span>
            </header>
          )}
          <Layout.Content className={styles.content}>
            {children}
          </Layout.Content>
        </Layout>
      </Layout>
      <Drawer
        title="Navigation"
        placement="left"
        width={280}
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        destroyOnClose
        rootClassName={styles.mobileDrawer}
      >
        <AppSidebar
          mobile
          collapsed={false}
          onCollapse={() => undefined}
          onNavigate={() => setMobileNavOpen(false)}
        />
      </Drawer>
      <ChangePasswordModal />
    </>
  );
};
